import { useCallback } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { PanelFooter } from 'foundations-components/transitional/components/PanelFooter';
import { Flex } from 'foundations-components/components/Flex';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useDataPermissions } from '../../contexts/DataPermissionsContext';
import { useUnsavedChanges, useRegisterUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { useUpdateAllowedAppPermissions } from '../../hooks/mutations/useUpdateAllowedAppPermissions';
import { useApp } from '../../contexts/AppContext';
import { useTracking } from '../../contexts/TrackingContext';
import { EVENTS, SCREENS, ACTIONS } from '../../constants/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function DataPermissionsFooter() {
  const {
    appId,
    appName
  } = useApp();
  const {
    updatedPermissionsCount,
    resetUpdatedPermissions,
    getCheckedOptionalPermissions,
    getCheckedConditionallyRequiredPermissions
  } = useDataPermissions();
  const {
    isDialogShowing
  } = useUnsavedChanges();
  const {
    updateAllowedAppPermissions,
    loading,
    error
  } = useUpdateAllowedAppPermissions();
  const {
    track
  } = useTracking();
  const handleSaveChanges = useCallback(async () => {
    const checkedOptionalPermissionIds = getCheckedOptionalPermissions().map(p => p.id);
    const checkedConditionallyRequiredPermissionIds = getCheckedConditionallyRequiredPermissions().map(p => p.id);
    await updateAllowedAppPermissions({
      variables: {
        appId,
        optionalAppPermissions: checkedOptionalPermissionIds,
        conditionallyRequiredAppPermissions: checkedConditionallyRequiredPermissionIds
      }
    }).then(() => {
      track(EVENTS.PANEL_INTERACTED, {
        screen: SCREENS.MANAGEMENT_MODAL_PANEL,
        action: ACTIONS.DATA_PERMISSIONS_SAVED,
        appId,
        appName
      });
      resetUpdatedPermissions();
    });
  }, [getCheckedOptionalPermissions, getCheckedConditionallyRequiredPermissions, updateAllowedAppPermissions, appId, appName, track, resetUpdatedPermissions]);
  const hasUnsavedChanges = useCallback(() => updatedPermissionsCount > 0, [updatedPermissionsCount]);
  useRegisterUnsavedChanges(hasUnsavedChanges, {
    onSave: handleSaveChanges,
    onDiscard: resetUpdatedPermissions,
    loading,
    failed: !!error
  });
  return /*#__PURE__*/_jsx(AccordionTransition, {
    open: updatedPermissionsCount > 0,
    children: /*#__PURE__*/_jsx(PanelFooter, {
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          use: "primary",
          onClick: () => void handleSaveChanges(),
          loading: !isDialogShowing && loading,
          failed: !isDialogShowing && !!error,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.dataPermissions.permissionsList.saveChanges.button"
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "secondary",
          onClick: resetUpdatedPermissions,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.dataPermissions.permissionsList.discardChanges.button"
          })
        }), /*#__PURE__*/_jsx(Paragraph, {
          color: "text-core-subtle",
          marginBlockEnd: "0",
          marginInlineStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.dataPermissions.permissionsList.saveChanges.permissionsModified",
            options: {
              count: updatedPermissionsCount
            }
          })
        })]
      })
    })
  });
}